// src/com/arrive_info/ArriveAutoViewOrder.js
import React, { useEffect, useState } from 'react';
import { Box } from '@mui/system';
import { styled } from '@mui/material/styles';
import {
    DialogContent, Button, DialogActions
} from '@mui/material';
import VerticalAlignBottomIcon from '@mui/icons-material/VerticalAlignBottom'; // 맨 뒤로 이동.
import VerticalAlignTopIcon from '@mui/icons-material/VerticalAlignTop'; // 맨 앞으로 이동.
import ClearIcon from '@mui/icons-material/Clear'; // cancel moving.
import HeightIcon from '@mui/icons-material/Height'; // 이동할 레코드 선택.
import ReadMoreIcon from '@mui/icons-material/ReadMore'; // insert above this record.
import DoneIcon from '@mui/icons-material/Done'; // close the dialog.
import SortByAlphaIcon from '@mui/icons-material/SortByAlpha';
import { useReactiveVar } from '@apollo/client';
import { useSnackbar } from 'notistack';
import SearchBox from '../../common/SearchBox';
import FlexyTable from '../../common/flexytable/FlexyTable';
import DraggableEditor from '../../common/DraggableEditor';
import { AppWord, AppObject, AppStyle } from '../../value/AppConst';
import { arriveInfoListRepo, authTableTreeRepo } from '../../value/AppModel';
import { ArriveInfoDictionary } from './ArriveInfoDict';
import { useAuthorizationCheck } from '../../api/app-hook';

const HeaderLine = styled(Box)({
    display: 'flex', alignItems: 'center', padding: 10, boxSizing: 'border-box',
});

const ActionText = styled(Box)({
    fontSize:'0.85rem', flexGrow: 1, display:'flex', alignItems:'center', testAlign: 'left',
    marginRight: 10, marginLeft: 10, color: '#fbb',
});

const GuideText = styled('div')({display:'inline-flex',alignItems:'center',color:AppStyle.PrimaryRGB});

const tableProps = ArriveInfoDictionary.autoOrderTableProps;

/**
 * 자동배처 처리 표시 순서 관리
 * @param {*} param0 
 * @returns 
 */
export default function ArriveAutoViewOrder({
    open,
    onClose,
    dcSelected,
    onRequestMove,
    response,
}) {
    const arriveInfoListData = useReactiveVar(arriveInfoListRepo);
    const [arriveOrderList, setArriveOrderList] = useState([]);
    const [moveState, setMoveState] = useState({moving: false, item: null, nextItem:null}); // item selected.
    const [search, setSearch] = useState('');
    const authTableTree = useReactiveVar(authTableTreeRepo);
    const checkAuth = useAuthorizationCheck();
    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
        if(open && dcSelected) {
            const viewOrderList = arriveInfoListData.records
                .filter(item => item.dcCd === dcSelected.dcCd && item.autoPlanYn === 'Y')
                .sort((a, b) => a.autoViewOrder - b.autoViewOrder);

            setArriveOrderList(viewOrderList);
            setSearch('');
            setMoveState({moving: false, item: {}, nextItem: {}});
        }
    }, [open, dcSelected, arriveInfoListData]);

    const clearMoveState = () => {
        setMoveState({moving: false, item: {}, nextItem: {}});
    };

    const requestMove = (movingItem, targetItem) => {
        // if targetItem is null, move to the last.
        // else insert before targetItem.
        const param = {
            arriveId: movingItem.arriveId,
            targetId: targetItem ? targetItem.arriveId : null,
        };
        onRequestMove(param);
        clearMoveState();
    };

    const onClickToFirst = () => {
        if(arriveOrderList.length === 0) return; // Not possible.
        const minTargetId = arriveOrderList[0].arriveId;
        const item = moveState.item;
        if(item.arriveId === minTargetId) {
            // cancel moving.
            enqueueSnackbar('이미 맨 앞에 있는 점포입니다.', {variant: 'info'});
            clearMoveState();
            return; // Already the first.
        }
        requestMove(item, arriveOrderList[0]);
    };

    const onClickToLast = () => {
        if(arriveOrderList.length === 0) return; // Not possible.
        const maxTargetId = arriveOrderList[arriveOrderList.length - 1].arriveId;
        const item = moveState.item;
        if(item.arriveId === maxTargetId) {
            // cancel moving.
            enqueueSnackbar('이미 맨 뒤에 있는 점포입니다.', {variant: 'info'});
            clearMoveState();
            return; // Already the last.
        }
        const param = {arriveId: item.arriveId, targetId: null};
        requestMove(param);
    };

    const onClickToMove = (item) => {
        setSearch('');
        // 자기 자신 뿐만 아니라 바로 다음 순서의 레코드로도 이동할 수 없다.
        // 따라서 그 레코드에도 icon이 나타나지 않도록 한다.
        const idx = arriveOrderList.findIndex((row)=>row.arriveId === item.arriveId);
        if(arriveOrderList.length > idx+1) {
            setMoveState({moving: true, item: item, nextItem: arriveOrderList[idx+1]});
        }
        else {
            setMoveState({moving: true, item: item, nextItem: {}});
        }
    };

    const onClickToolOnRow = (toolId, row) => {
        switch(toolId) {
            case AppObject.command.MOVE:
                onClickToMove(row);
                break;
            case 'set':
                requestMove(moveState.item, row);
                break;
            case 'cancel':
                clearMoveState();
                break;
            default: return;
        }
    };

    const canSetAutoViewOrder = checkAuth(authTableTree.tree.pr_arrive_info_set_auto_view_order);
    const toolsWaiting = canSetAutoViewOrder ? [
        {id:AppObject.command.MOVE, icon:<HeightIcon color={AppStyle.mui.primary}/>, title:'순서변경'},
    ] : [];

    const toolsMoving = canSetAutoViewOrder ? [
        {
            id:'set', icon:<ReadMoreIcon color={AppStyle.mui.primary}/>, title:'이 위에 끼워넣기',
            where: (row)=>row.arriveId !== moveState.item.arriveId && row.arriveId !== moveState.nextItem.arriveId
        },
        {
            id:'cancel', icon:<ClearIcon color={AppStyle.mui.secondary}/>, title:'취소',
            where: (row)=>row.arriveId === moveState.item.arriveId || row.arriveId === moveState.nextItem.arriveId
        },
    ] : [];

    return(
        <DraggableEditor open={open} maxWidth='lg'
            startIcon={<SortByAlphaIcon size={AppWord.LARGE} />}
            title="자동배차 처리 표시 순서 관리"
        >
            <DialogContent style={{position: 'relative', minHeight:'50vh', minWidth:500, width:'50vw'}}>
                <Box style={{position:'absolute', left:5, top:0, right:5, bottom:3, display:'flex', flexDirection: 'column'}}>
                    <HeaderLine style={{minHeight:65}}>
                        {
                            moveState.moving ?
                            <>
                                <Button
                                    variant={AppStyle.mui.contained}
                                    color={AppStyle.mui.primary}
                                    onClick={onClickToFirst}
                                    startIcon={<VerticalAlignTopIcon />}
                                >
                                    맨 앞으로
                                </Button>
                                <Button
                                    variant={AppStyle.mui.contained}
                                    color={AppStyle.mui.secondary}
                                    onClick={onClickToLast}
                                    startIcon={<VerticalAlignBottomIcon />}
                                    style={{marginLeft: 10}}
                                >
                                    맨 뒤로
                                </Button>
                                <Button
                                    variant={AppStyle.mui.contained}
                                    color={AppStyle.mui.success}
                                    onClick={clearMoveState}
                                    startIcon={<ClearIcon />}
                                    style={{marginLeft: 10}}
                                >
                                    취소
                                </Button>
                            </>
                            :
                            <SearchBox value={search} onChangeText={setSearch} disabled={moveState.moving}/>
                        }
                        
                    </HeaderLine>
                    <HeaderLine>
                        {
                            moveState.moving ?
                            <GuideText>
                                <span>선택한 {moveState.item ? moveState.item.storeName : ''} 점포를 이동할 위치의 </span>
                                    <ReadMoreIcon fontSize={AppWord.SMALL} color={AppStyle.mui.primary} />
                                    <span> 아이콘을 클릭하세요.</span>
                            </GuideText>
                            :
                            <GuideText>
                                <span>순서를 변경할 도착지(점포) 줄의 맨 끝에 나타나는 </span>
                                <HeightIcon fontSize={AppWord.SMALL} color={AppStyle.mui.primary} />
                                <span>아이콘을 클릭하세요.</span>
                            </GuideText>
                        }
                    </HeaderLine>
                    <FlexyTable
                        name={tableProps.tableId}
                        tableProps={tableProps}
                        records={arriveOrderList}
                        search={search}
                        tools={moveState.moving ? toolsMoving : toolsWaiting}
                        onClickTool={onClickToolOnRow}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <ActionText>
                    {
                        response.loading ?
                        "처리중입니다. 잠시만 기다려주세요." : null
                    }
                </ActionText>
                <Button
                    variant={AppStyle.mui.contained}
                    color={AppStyle.mui.primary}
                    onClick={()=>onClose()}
                    startIcon={<DoneIcon />}
                    disabled={response.loading}
                >
                    닫기
                </Button>
            </DialogActions>
        </DraggableEditor>
    );
}
